import React, { useState } from 'react';
import { Outlet, useLocation, Link, useParams } from 'react-router-dom';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
} from './ui/sidebar';
import { DashboardControls } from './DashboardControls';
import { UserProfileDropdown } from './UserProfileDropdown';
import { OrgSwitcher } from './OrgSwitcher';
import { useAuth } from '../contexts/AuthContext';
import { useOrganization } from '../contexts/OrganizationContext';
import { Button } from './ui/button';
import { GlobalBanner } from './GlobalBanner';
import {
  Home,
  MonitorPlay,
  Database,
  BarChart3,
  Wallet,
} from 'lucide-react';
import {
  FaMeta,
  FaGoogle,
  FaPinterest,
  FaMicrosoft,
  FaRedditAlien
} from 'react-icons/fa6';

// Analyst-only link to cross-org Analyst Dashboard
function AnalystDashboardLink() {
  const { isAnalyst, isBatman } = useAuth();
  const location = useLocation();

  // Sticky visibility: once shown, stay shown. Prevents the link flickering out
  // while isAnalyst/isBatman briefly resolve to false during the auth cascade.
  //
  // This uses state adjusted during render rather than a ref. Writing a ref during
  // render is unsafe under concurrent rendering: a render that React discards
  // (StrictMode double-invoke, an interrupted transition) still mutates the ref,
  // so the latch can set from a render that never committed. Adjusting state
  // during render is the pattern React documents for exactly this, and is
  // idempotent because React re-runs the component before committing.
  const [hasBeenVisible, setHasBeenVisible] = useState(false);
  const shouldShow = isAnalyst || isBatman;
  if (shouldShow && !hasBeenVisible) {
    setHasBeenVisible(true);
  }

  if (!shouldShow && !hasBeenVisible) {
    return null;
  }

  const isActive = location.pathname === '/analyst';

  return (
    <div className="px-2 py-2">
      <Button
        variant={isActive ? 'secondary' : 'outline'}
        className="w-full justify-start gap-2"
        asChild
      >
        <Link to="/analyst">
          <BarChart3 className="size-4" />
          Analyst Dashboard
        </Link>
      </Button>
    </div>
  );
}

// Batman-only link to Admin Dashboard
function AdminDashboardLink({ basePath }: { basePath: string }) {
  const { isBatman } = useAuth();
  const location = useLocation();

  // Sticky visibility — see the note in AnalystDashboardLink above.
  const [hasBeenVisible, setHasBeenVisible] = useState(false);
  if (isBatman && !hasBeenVisible) {
    setHasBeenVisible(true);
  }

  if (!isBatman && !hasBeenVisible) {
    return null;
  }

  const isActive = location.pathname === `${basePath}/admin`;

  return (
    <div className="px-2 py-2">
      <Button
        variant={isActive ? 'secondary' : 'outline'}
        className="w-full justify-start gap-2"
        asChild
      >
        <Link to={`${basePath}/admin`}>
          <Database className="size-4" />
          Admin Dashboard
        </Link>
      </Button>
    </div>
  );
}

interface AppLayoutProps {
  filters: any;
  onFiltersChange: (filters: any) => void;
  onRefresh: () => void;
  onExportCSV: () => void;
  isLoading: boolean;
  totalCount: number;
  filteredCount: number;
}

export function AppLayout({
  filters,
  onFiltersChange,
  onRefresh,
  onExportCSV,
  isLoading,
  totalCount,
  filteredCount
}: AppLayoutProps) {
  const location = useLocation();
  const { orgSlug } = useParams<{ orgSlug: string }>();
  const { currentOrganization } = useOrganization();

  const basePath = `/${orgSlug}`;
  const hasCustomReports = currentOrganization?.settings?.has_custom_reports === true;

  const navigationItems = [
    {
      title: "Overview",
      url: basePath,
      icon: Home,
      isActive: location.pathname === basePath || location.pathname === `${basePath}/`,
      isComingSoon: false
    },
    {
      title: "Meta Dashboard",
      url: `${basePath}/meta-dashboard`,
      icon: FaMeta,
      isActive: location.pathname === `${basePath}/meta-dashboard`,
      isComingSoon: false
    },
    {
      title: "Google Dashboard",
      url: `${basePath}/google-dashboard`,
      icon: FaGoogle,
      isActive: location.pathname === `${basePath}/google-dashboard`,
      isComingSoon: false
    },
    // Conditionally add Paid Media Breakdown for orgs with custom reports
    ...(hasCustomReports ? [{
      title: "Paid Media Breakdown",
      url: `${basePath}/paid-media-breakdown`,
      icon: BarChart3,
      isActive: location.pathname === `${basePath}/paid-media-breakdown`,
      isComingSoon: false
    }] : []),
    {
      title: "Pinterest",
      url: `${basePath}/pinterest-dashboard`,
      icon: FaPinterest,
      isActive: location.pathname === `${basePath}/pinterest-dashboard`,
      isComingSoon: false
    },
    {
      title: "Budgets",
      url: `${basePath}/budgets`,
      icon: Wallet,
      isActive: location.pathname === `${basePath}/budgets`,
      isComingSoon: false
    },
    {
      title: "Microsoft Ads",
      url: "#",
      icon: FaMicrosoft,
      isActive: false,
      isComingSoon: true
    },
    {
      title: "OTT",
      url: "#",
      icon: MonitorPlay,
      isActive: false,
      isComingSoon: true
    },
    {
      title: "Reddit",
      url: "#",
      icon: FaRedditAlien,
      isActive: false,
      isComingSoon: true
    }
  ];

  return (
    <div className="flex flex-col min-h-screen">
      {/* System Banners - Full width above everything */}
      <GlobalBanner />

      <SidebarProvider>
        <div className="flex flex-1 w-full">
        {/* Left Sidebar */}
        <Sidebar variant="inset">
          <SidebarHeader>
            <div className="flex items-center gap-2 px-2 py-2">
              <span className="text-lg font-semibold">AdCanvas</span>
            </div>
          </SidebarHeader>

          <SidebarContent>
            <SidebarGroup>
              <SidebarGroupLabel>Platforms</SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  {navigationItems.map((item) => (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton
                        asChild={!item.isComingSoon}
                        isActive={item.isActive}
                        className={item.isComingSoon ? 'opacity-60 cursor-not-allowed pointer-events-none' : ''}
                      >
                        {item.isComingSoon ? (
                          <div className="flex items-center gap-2">
                            <item.icon className="size-4" />
                            <span>{item.title}</span>
                            <span className="ml-auto text-xs text-muted-foreground">Soon</span>
                          </div>
                        ) : (
                          <Link to={item.url} className="flex items-center gap-2">
                            <item.icon className="size-4" />
                            <span>{item.title}</span>
                          </Link>
                        )}
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </SidebarContent>

          <SidebarFooter>
            <AnalystDashboardLink />
            <AdminDashboardLink basePath={basePath} />
            <OrgSwitcher />
            <UserProfileDropdown />
          </SidebarFooter>
        </Sidebar>

        {/* Main Content Area */}
        <SidebarInset className={location.pathname.endsWith('/meta-dashboard') ? 'dux-library-shell' : location.pathname.endsWith('/google-dashboard') ? 'gcw-library-shell' : undefined}>
          {/* Global Header */}
          <header className="flex h-16 shrink-0 items-center gap-2 border-b px-4">
            <SidebarTrigger className="-ml-1" />
            <div className="flex-1">
              {/* Only show DashboardControls on non-Overview pages */}
              {location.pathname !== basePath && location.pathname !== `${basePath}/` && (
                <DashboardControls
                  filters={filters}
                  onFiltersChange={onFiltersChange}
                  onRefresh={onRefresh}
                  onExportCSV={onExportCSV}
                  isLoading={isLoading}
                  totalCount={totalCount}
                  filteredCount={filteredCount}
                  showAdvancedFilters={!location.pathname.endsWith('/google-dashboard')}
                />
              )}
            </div>
          </header>

          {/* Page Content */}
          <div className="flex flex-1 flex-col gap-4 p-4">
            <Outlet />
          </div>
        </SidebarInset>
        </div>
      </SidebarProvider>
    </div>
  );
}
